import React from "react";
import { Link } from "react-router-dom";
import sueoLogo from "./dailysign_logo.png";
import shot1 from "./screenshots/dailysign-1.png";
import shot2 from "./screenshots/dailysign-2.png";
import shot3 from "./screenshots/dailysign-3.png";

const SUEO = "#7C67F2";
const SUEO_DEEP = "#5F4BD8";

const copy = {
  navTerms: "이용약관",
  navPrivacy: "개인정보 처리방침",
  overline: "iOS · Android · 한국수어(KSL)",
  title: ["하루 5단어,", "가족과 함께 배우는 수어."],
  lead:
    "매일수어는 가족과 같은 언어로 대화하고 싶은 모든 분을 위한 한국수어 학습 앱입니다. 국립국어원 한국수어사전의 실제 수어 영상으로, 가족 호칭부터 감정·일상 표현까지 생활 순서 그대로 배웁니다.",
  comingSoon: "App Store · Google Play 출시 예정",
  platformNote: "회원가입 없음 · 구독 없음 · 전체 잠금해제는 일회성 결제 · 학습 기록은 기기 안에만",
  philosophyLabel: "왜 매일수어인가요",
  philosophy: [
    {
      eyebrow: "Daily 5",
      title: "부담 없는 하루 5단어",
      body: "매일 5단어씩, 10분이면 충분한 데일리 루틴입니다. 연속 학습 스트릭이 쌓이며 꾸준함이 눈에 보입니다.",
    },
    {
      eyebrow: "Real KSL",
      title: "진짜 수어 영상으로",
      body: "국립국어원 한국수어사전의 실제 수어 영상 300개를 반복재생·0.5배속으로 배웁니다. 삽화가 아닌 진짜 손말입니다.",
    },
    {
      eyebrow: "Mirror",
      title: "거울처럼 따라하기",
      body: "영상과 내 모습을 나란히 보며 연습합니다. 카메라는 미리보기 전용 — 촬영도, 저장도, 전송도 하지 않습니다.",
    },
    {
      eyebrow: "Curriculum",
      title: "생활 순서로 짠 20단계",
      body: "가족 호칭 → 인사 → 감정 → 식사·일상… 실제 생활에서 쓰는 순서 그대로 300단어를 20단계로 배웁니다.",
    },
  ],
  previewLabel: "미리보기",
  featuresLabel: "전체 기능",
  features: [
    ["오늘의 5단어", "매일 이어지는 데일리 학습과 스트릭"],
    ["수어 영상", "반복재생 · 0.5배속 · 단어 검색"],
    ["따라하기", "영상과 카메라 미러로 나란히 연습"],
    ["20단계 커리큘럼", "가족 호칭부터 생활 순서로 300단어"],
    ["복습 퀴즈", "배운 단어를 퀴즈로 확인"],
    ["진도·즐겨찾기", "단계별 진도와 자주 보는 단어 모아보기"],
  ],
  sourceNote:
    "수어 단어·영상은 국립국어원 「한국수어사전」 공공데이터(공공누리 제1유형)를 활용했습니다. 본 앱은 국립국어원과 무관한 독립 서비스입니다.",
  footerHome: "홈으로",
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f0edfe 0%, #ffffff 36%)",
    color: "var(--ink)",
  },
  shell: {
    width: "100%",
    maxWidth: 1120,
    margin: "0 auto",
    padding: "28px 20px 64px",
    boxSizing: "border-box",
  },
  nav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    marginBottom: 40,
    flexWrap: "wrap",
  },
  navBrand: {
    color: SUEO_DEEP,
    textDecoration: "none",
    fontSize: 14,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    fontWeight: 800,
  },
  navLinks: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "center",
    fontSize: 14,
  },
  navLink: {
    color: "var(--ink-2)",
    textDecoration: "none",
    fontWeight: 600,
  },
  hero: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: 36,
    alignItems: "center",
    padding: "8px 0 40px",
  },
  overline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 14px",
    borderRadius: 999,
    background: "rgba(124, 103, 242, 0.1)",
    border: "1px solid rgba(124, 103, 242, 0.2)",
    color: SUEO_DEEP,
    fontSize: 12,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    fontWeight: 700,
  },
  appId: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    margin: "22px 0 16px",
  },
  appLogo: {
    width: 64,
    height: 64,
    borderRadius: 16,
    objectFit: "cover",
    boxShadow: "0 10px 24px rgba(95, 75, 216, 0.24)",
  },
  appName: {
    margin: 0,
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: -0.3,
  },
  appCategory: {
    margin: "3px 0 0",
    fontSize: 13,
    color: "var(--ink-3)",
  },
  title: {
    margin: "6px 0 16px",
    fontSize: "clamp(2.2rem, 5.6vw, 3.9rem)",
    lineHeight: 1.1,
    letterSpacing: "-0.03em",
    fontWeight: 800,
    color: "var(--ink)",
  },
  titleAccent: {
    color: SUEO,
  },
  lead: {
    margin: "0 0 18px",
    fontSize: 16,
    lineHeight: 1.8,
    color: "var(--ink-2)",
    maxWidth: 560,
  },
  badgeRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    marginTop: 24,
  },
  comingSoonBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    minHeight: 50,
    padding: "14px 26px",
    borderRadius: 999,
    background: `linear-gradient(135deg, #8f7bf7 0%, ${SUEO_DEEP} 100%)`,
    color: "#fff",
    fontSize: 15,
    fontWeight: 700,
    boxShadow: "0 14px 30px rgba(124, 103, 242, 0.32)",
  },
  note: {
    marginTop: 16,
    fontSize: 13,
    lineHeight: 1.7,
    color: "var(--ink-3)",
  },
  heroVisualWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  halo: {
    position: "absolute",
    width: 380,
    height: 380,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(124, 103, 242, 0.18) 0%, rgba(124, 103, 242, 0.05) 46%, rgba(124, 103, 242, 0) 74%)",
    filter: "blur(4px)",
  },
  heroShot: {
    position: "relative",
    width: "min(100%, 340px)",
    borderRadius: 28,
    border: "1px solid var(--rule-soft)",
    boxShadow: "0 28px 60px rgba(95, 75, 216, 0.2)",
  },
  sectionLabel: {
    margin: "56px 0 20px",
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    color: SUEO_DEEP,
  },
  philosophyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 16,
  },
  philosophyCard: {
    background: "#fff",
    border: "1px solid var(--rule-soft)",
    borderRadius: 20,
    padding: "22px 22px 20px",
    boxShadow: "0 12px 30px rgba(31, 42, 68, 0.07)",
  },
  cardEyebrow: {
    margin: "0 0 10px",
    fontSize: 11.5,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: SUEO,
    fontWeight: 700,
  },
  cardTitle: {
    margin: "0 0 8px",
    fontSize: 17.5,
    fontWeight: 800,
    color: "var(--ink)",
  },
  cardText: {
    margin: 0,
    fontSize: 13.5,
    lineHeight: 1.7,
    color: "var(--ink-2)",
  },
  shotsRow: {
    display: "flex",
    gap: 18,
    justifyContent: "center",
    flexWrap: "wrap",
    marginTop: 8,
  },
  shotImg: {
    width: "min(100%, 260px)",
    borderRadius: 22,
    border: "1px solid var(--rule-soft)",
    boxShadow: "0 20px 44px rgba(95, 75, 216, 0.16)",
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 12,
  },
  featureCard: {
    background: "var(--bg-soft)",
    border: "1px solid var(--rule-soft)",
    borderRadius: 16,
    padding: "16px 18px",
  },
  featureTitle: {
    margin: "0 0 6px",
    fontSize: 14.5,
    fontWeight: 800,
    color: SUEO_DEEP,
  },
  featureBody: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.65,
    color: "var(--ink-2)",
  },
  sourceNote: {
    marginTop: 40,
    padding: "14px 18px",
    borderRadius: 14,
    background: "rgba(124, 103, 242, 0.06)",
    border: "1px solid rgba(124, 103, 242, 0.16)",
    fontSize: 12.5,
    lineHeight: 1.7,
    color: "var(--ink-3)",
  },
  footer: {
    marginTop: 48,
    paddingTop: 18,
    borderTop: "1px solid var(--rule-soft)",
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "center",
    color: "var(--ink-3)",
    fontSize: 13,
  },
};

const DailySignLanding: React.FC = () => {
  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <nav style={styles.nav}>
          <Link to="/" style={styles.navBrand}>
            Daydream Factory
          </Link>
          <div style={styles.navLinks}>
            <Link to="/dailysign/terms" style={styles.navLink}>
              {copy.navTerms}
            </Link>
            <Link to="/dailysign/privacy" style={styles.navLink}>
              {copy.navPrivacy}
            </Link>
          </div>
        </nav>

        <section style={styles.hero}>
          <div>
            <span style={styles.overline}>{copy.overline}</span>

            <div style={styles.appId}>
              <img src={sueoLogo} alt="매일수어 앱 아이콘" style={styles.appLogo} />
              <div>
                <p style={styles.appName}>매일수어</p>
                <p style={styles.appCategory}>Korean Sign Language · Education</p>
              </div>
            </div>

            <h1 style={styles.title}>
              {copy.title[0]}
              <br />
              <span style={styles.titleAccent}>{copy.title[1]}</span>
            </h1>
            <p style={styles.lead}>{copy.lead}</p>

            <div style={styles.badgeRow}>
              <span style={styles.comingSoonBadge}>✦ {copy.comingSoon}</span>
            </div>

            <p style={styles.note}>{copy.platformNote}</p>
          </div>

          <div style={styles.heroVisualWrap}>
            <div style={styles.halo} />
            <img src={shot1} alt="매일수어 오늘의 학습 화면" style={styles.heroShot} />
          </div>
        </section>

        <section>
          <p style={styles.sectionLabel}>{copy.philosophyLabel}</p>
          <div style={styles.philosophyGrid}>
            {copy.philosophy.map((card) => (
              <article key={card.title} style={styles.philosophyCard}>
                <p style={styles.cardEyebrow}>{card.eyebrow}</p>
                <h3 style={styles.cardTitle}>{card.title}</h3>
                <p style={styles.cardText}>{card.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section>
          <p style={styles.sectionLabel}>{copy.previewLabel}</p>
          <div style={styles.shotsRow}>
            {[shot1, shot2, shot3].map((src, index) => (
              <img key={src} src={src} alt={`매일수어 스크린샷 ${index + 1}`} style={styles.shotImg} />
            ))}
          </div>
        </section>

        <section>
          <p style={styles.sectionLabel}>{copy.featuresLabel}</p>
          <div style={styles.featureGrid}>
            {copy.features.map(([title, body]) => (
              <article key={title} style={styles.featureCard}>
                <h3 style={styles.featureTitle}>{title}</h3>
                <p style={styles.featureBody}>{body}</p>
              </article>
            ))}
          </div>

          <p style={styles.sourceNote}>{copy.sourceNote}</p>
        </section>

        <footer style={styles.footer}>
          <span>support@daydreamfactory.net</span>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <Link to="/dailysign/terms" style={styles.navLink}>
              {copy.navTerms}
            </Link>
            <Link to="/dailysign/privacy" style={styles.navLink}>
              {copy.navPrivacy}
            </Link>
            <Link to="/" style={styles.navLink}>
              {copy.footerHome}
            </Link>
          </div>
        </footer>
      </div>
    </main>
  );
};

export default DailySignLanding;
